"use client";

import { Button, IconButton } from "../../shared";
import { Box, Card, Divider, Sheet, Typography, useTheme } from "@mui/joy";
import { AnimatePresence, motion } from "framer-motion";
import {
    Eclipse,
    Ellipsis,
    ListTodo,
    Maximize2,
    Share,
    Share2,
    X,
} from "lucide-react";
import { createPortal } from "react-dom";

const MotionBox = motion.create(Box);

export default function SideModal({
    open,
    onClose,
    children,
    portalBoxProps = {},
    showAdditionalHeadersIcons = true,
    hideHeader = false,
    title = "",
    description = "",
}: ISideModal) {
    const theme = useTheme(); // Get current theme

    return createPortal(
        <AnimatePresence>
            {open && (
                <MotionBox
                    key="side-modal"
                    initial={{ x: "100%" }}
                    animate={{ x: 0 }}
                    exit={{ x: "100%" }}
                    transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
                    sx={{
                        position: "fixed",
                        top: 0,
                        right: 0,
                        width: "700px", // Adjust modal width
                        height: "100vh",
                        zIndex: 1010, // Ensure it's on top
                        ...portalBoxProps,
                    }}
                >
                    {/* Side Panel */}
                    <Card
                        sx={{
                            position: "relative",
                            height: "100%",
                            width: "100%",
                            border: "none",
                            gap: 0,
                            p: 0,
                            borderRadius: 0, // No rounded corners
                            // backgroundColor:
                            //     theme.palette.mode === "dark"
                            //         ? "rgba(20, 20, 20, 0.7)" // Dark frosted effect
                            //         : "rgba(255, 255, 255, 0.6)", // Light frosted effect
                            backdropFilter: "blur(10px)", // Frosted glass effect inside modal
                            WebkitBackdropFilter: "blur(10px)", // Safari support
                            boxShadow: "0px 0px 78px rgba(0, 0, 0, 0.3)", // Left-side shadow
                            color:
                                theme.palette.mode === "dark" ? "#fff" : "#000", // Ensure readable text
                            overflow: "auto", // Allow scrolling if content overflows
                        }}
                    >
                        {!hideHeader && (
                            <Sheet
                                sx={{
                                    position: "sticky",
                                    top: 0,
                                    pt: 1.5,
                                    zIndex: 1001,
                                }}
                            >
                                <Box
                                    display={"flex"}
                                    justifyContent={"space-between"}
                                    alignItems={"center"}
                                >
                                    {title && (
                                        <Box
                                            display={"flex"}
                                            alignItems={"center"}
                                            sx={{ ml: 2 }}
                                            gap={1.5}
                                        >
                                            <Card sx={{ p: 0.7 }}>
                                                <ListTodo size={20} />
                                            </Card>

                                            <Box>
                                                <Typography level="title-lg">
                                                    {title}
                                                </Typography>
                                                <Typography level="body-sm">
                                                    {description}{" "}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                    {showAdditionalHeadersIcons && (
                                        <Box
                                            display={"flex"}
                                            pl={2}
                                            gap={1}
                                            alignItems={"center"}
                                        >
                                            <Button
                                                startDecorator={
                                                    <Share2 size={18} />
                                                }
                                                variant={"plain"}
                                            >
                                                Share
                                            </Button>
                                            <Button
                                                startDecorator={
                                                    <Maximize2 size={18} />
                                                }
                                                variant={"plain"}
                                            >
                                                Expand
                                            </Button>
                                            <IconButton variant={"plain"}>
                                                <Ellipsis />
                                            </IconButton>
                                        </Box>
                                    )}
                                    <IconButton
                                        variant={"plain"}
                                        sx={{ ml: 2, mr: 1.5 }}
                                        onClick={onClose}
                                    >
                                        <X />
                                    </IconButton>
                                </Box>
                                <Divider inset="context" sx={{ mt: 1.5 }} />
                            </Sheet>
                        )}

                        {children}
                    </Card>
                </MotionBox>
            )}
        </AnimatePresence>,
        document.getElementById("layout-root") || document.body, // Moves it outside of all stacking contexts
    );
}

interface ISideModal {
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
    portalBoxProps?: any;
    showAdditionalHeadersIcons?: boolean;
    title?: string;
    description?: string;
    hideHeader?: boolean;
}
